import { useEffect, useMemo, useRef } from 'react';
import type { ColorGrade, ColorWheel as Wheel } from '@cinegrid/core';
import { defaultColorGrade, defaultColorWheel } from '@cinegrid/core';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell, SectionLabel, SliderRow } from '../controls/PanelShell';

function ColorWheelControl({
  label,
  wheel,
  onChange,
}: {
  label: string;
  wheel: Wheel;
  onChange: (w: Partial<Wheel>) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const size = 96;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 4;
    ctx.clearRect(0, 0, size, size);
    for (let a = 0; a < 360; a += 2) {
      const rad = (a * Math.PI) / 180;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, rad, rad + (2.5 * Math.PI) / 180);
      ctx.closePath();
      ctx.fillStyle = `hsl(${a}, 70%, 45%)`;
      ctx.fill();
    }
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.35, 0, Math.PI * 2);
    ctx.fillStyle =
      getComputedStyle(document.documentElement).getPropertyValue('--cg-bg-raised').trim() ||
      '#282c31';
    ctx.fill();

    const ix = cx + wheel.r * r * 0.7 - wheel.b * r * 0.35;
    const iy = cy - wheel.g * r * 0.7;
    ctx.beginPath();
    ctx.arc(ix, iy, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.fill();
    ctx.stroke();
  }, [wheel.r, wheel.g, wheel.b]);

  const onPointer = (e: React.PointerEvent) => {
    const canvas = ref.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    const mag = Math.min(1, Math.hypot(x, y));
    const nx = (x / (mag || 1)) * mag;
    const ny = (y / (mag || 1)) * mag;
    onChange({
      r: Math.max(-1, Math.min(1, nx * 0.8 + ny * 0.2)),
      g: Math.max(-1, Math.min(1, ny)),
      b: Math.max(-1, Math.min(1, -nx * 0.8 + ny * 0.2)),
    });
  };

  const reset = () => onChange(defaultColorWheel());

  return (
    <div className="flex flex-col items-center gap-1" title="Double-click wheel to reset">
      <canvas
        ref={ref}
        style={{ width: size, height: size }}
        className="cursor-crosshair"
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          onPointer(e);
        }}
        onPointerMove={(e) => {
          if (e.buttons) onPointer(e);
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          reset();
        }}
      />
      <span
        className="cursor-pointer text-2xs text-text-low hover:text-accent"
        onDoubleClick={reset}
      >
        {label}
      </span>
      <SliderRow
        label="Master"
        value={wheel.master}
        min={-1}
        max={1}
        defaultValue={0}
        onChange={(master) => onChange({ master })}
      />
    </div>
  );
}

export function ColorPanel() {
  const selection = useEditorStore((s) => s.selection);
  const project = useEditorStore((s) => s.project);
  const updateClipColor = useEditorStore((s) => s.updateClipColor);

  const clip = useMemo(
    () => project.clips.find((c) => c.id === selection[0]),
    [project.clips, selection]
  );
  const grade: ColorGrade = clip?.properties.color ?? defaultColorGrade();

  if (!clip) {
    return (
      <PanelShell title="Color">
        <p className="p-3 text-2xs text-text-low">Select a clip to grade.</p>
      </PanelShell>
    );
  }

  const patch = (p: Partial<ColorGrade>) => updateClipColor(clip.id, p);
  const defaults = defaultColorGrade();

  return (
    <PanelShell
      title="Color"
      actions={
        <button
          type="button"
          className="text-2xs text-text-low hover:text-accent"
          onClick={() => updateClipColor(clip.id, defaultColorGrade())}
        >
          Reset
        </button>
      }
    >
      <SectionLabel>Wheels — Lift / Gamma / Gain</SectionLabel>
      <div className="grid grid-cols-3 gap-1 border-b border-hairline p-2">
        <ColorWheelControl
          label="Shadows"
          wheel={grade.lift}
          onChange={(w) => patch({ lift: { ...grade.lift, ...w } })}
        />
        <ColorWheelControl
          label="Midtones"
          wheel={grade.gamma}
          onChange={(w) => patch({ gamma: { ...grade.gamma, ...w } })}
        />
        <ColorWheelControl
          label="Highlights"
          wheel={grade.gain}
          onChange={(w) => patch({ gain: { ...grade.gain, ...w } })}
        />
      </div>

      <SectionLabel>Tonal</SectionLabel>
      <SliderRow label="Exposure" value={grade.exposure} min={-3} max={3} defaultValue={defaults.exposure} onChange={(exposure) => patch({ exposure })} />
      <SliderRow label="Contrast" value={grade.contrast} min={0} max={2} defaultValue={defaults.contrast} onChange={(contrast) => patch({ contrast })} />
      <SliderRow label="Shadows" value={grade.shadows} defaultValue={defaults.shadows} onChange={(shadows) => patch({ shadows })} />
      <SliderRow label="Midtones" value={grade.midtones} defaultValue={defaults.midtones} onChange={(midtones) => patch({ midtones })} />
      <SliderRow label="Highlights" value={grade.highlights} defaultValue={defaults.highlights} onChange={(highlights) => patch({ highlights })} />
      <SliderRow label="Whites" value={grade.whites} defaultValue={defaults.whites} onChange={(whites) => patch({ whites })} />
      <SliderRow label="Blacks" value={grade.blacks} defaultValue={defaults.blacks} onChange={(blacks) => patch({ blacks })} />
      <SliderRow label="Intensity" value={grade.intensity} min={0} max={1} defaultValue={defaults.intensity} onChange={(intensity) => patch({ intensity })} />

      <SectionLabel>Color</SectionLabel>
      <SliderRow label="Temp" value={grade.temperature} defaultValue={defaults.temperature} onChange={(temperature) => patch({ temperature })} />
      <SliderRow label="Tint" value={grade.tint} defaultValue={defaults.tint} onChange={(tint) => patch({ tint })} />
      <SliderRow label="Saturation" value={grade.saturation} min={0} max={2} defaultValue={defaults.saturation} onChange={(saturation) => patch({ saturation })} />
      <SliderRow label="Hue" value={grade.hue} min={-180} max={180} step={1} defaultValue={defaults.hue} onChange={(hue) => patch({ hue })} format={(v) => `${v | 0}°`} />

      <SectionLabel>Qualifier</SectionLabel>
      <div className="flex items-center gap-2 px-2 py-1">
        <label className="flex items-center gap-1.5 text-2xs text-text-low">
          <input
            type="checkbox"
            checked={grade.qualifier.enabled}
            onChange={(e) =>
              patch({ qualifier: { ...grade.qualifier, enabled: e.target.checked } })
            }
          />
          Enable
        </label>
        <input
          type="color"
          value={grade.qualifier.pickHex}
          title="Color picker"
          onChange={(e) => {
            const hex = e.target.value;
            const r = parseInt(hex.slice(1, 3), 16) / 255;
            const g = parseInt(hex.slice(3, 5), 16) / 255;
            const b = parseInt(hex.slice(5, 7), 16) / 255;
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            let h = 0;
            const d = max - min;
            if (d) {
              if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
              else if (max === g) h = ((b - r) / d + 2) * 60;
              else h = ((r - g) / d + 4) * 60;
            }
            const soft = 25;
            patch({
              qualifier: {
                ...grade.qualifier,
                pickHex: hex,
                enabled: true,
                hueMin: (h - soft + 360) % 360,
                hueMax: (h + soft) % 360,
              },
            });
          }}
          className="h-6 w-8 cursor-pointer border border-hairline bg-transparent"
        />
        <label className="flex items-center gap-1.5 text-2xs text-text-low">
          <input
            type="checkbox"
            checked={grade.qualifier.invert}
            onChange={(e) =>
              patch({ qualifier: { ...grade.qualifier, invert: e.target.checked } })
            }
          />
          Invert
        </label>
      </div>
      <SliderRow
        label="Soft"
        value={grade.qualifier.soft}
        min={0}
        max={1}
        defaultValue={defaults.qualifier.soft}
        onChange={(soft) => patch({ qualifier: { ...grade.qualifier, soft } })}
      />
    </PanelShell>
  );
}
